import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import banner from '../assets/tpp-claims-banner.jpg';
import Layout from '../components/Layout';
import ProgressSteps from '../components/ProgressSteps';
import { RootState } from '../store/store';
import { getInventoryItems, updateInventoryItem } from '../services/api';
import { useClaimTokenResume } from '../hooks/useClaimTokenResume';
import { buildClaimResumePath, claimStatusReturnPath } from '../utils/claimTokenResume';
import { getErrorMessage } from '../utils/errorHandler';
import { useSnackbar } from '../contexts/SnackbarContext';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 80 }, (_, i) => String(currentYear - i));
const months = [
  { label: 'Jan', value: '01' },
  { label: 'Feb', value: '02' },
  { label: 'Mar', value: '03' },
  { label: 'Apr', value: '04' },
  { label: 'May', value: '05' },
  { label: 'Jun', value: '06' },
  { label: 'Jul', value: '07' },
  { label: 'Aug', value: '08' },
  { label: 'Sep', value: '09' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' },
];

const normalizeDecimalInput = (raw: string): string =>
  String(raw || '').replace(/,/g, '').replace(/\$/g, '').trim();

const EditInventoryItem: React.FC = () => {
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const claimData = useSelector((state: RootState) => state.claim);
  const { showSuccess, showError } = useSnackbar();
  const { claimToken, fromClaimStatus, resumeError, isClaimReady, isIncomplete } = useClaimTokenResume();
  const incompleteFromStatus = fromClaimStatus && isIncomplete;

  const claimId = claimData.claimId;
  const editingItemId = itemId ? Number(itemId) : null;

  const [isSavingItem, setIsSavingItem] = useState(false);
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [itemDescription, setItemDescription] = useState('');
  const [damageDescription, setDamageDescription] = useState('');
  const [purchaseMonth, setPurchaseMonth] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [price, setPrice] = useState('');

  const listPath = buildClaimResumePath('/damages', claimToken, fromClaimStatus);

  const handleCancel = () => {
    navigate(listPath);
  };

  useEffect(() => {
    if (!claimId || !editingItemId || Number.isNaN(editingItemId)) {
      return;
    }

    let cancelled = false;
    setIsLoadingItem(true);
    getInventoryItems(claimId)
      .then((items) => {
        if (cancelled) return;
        const item = items.find((entry) => entry.id === editingItemId);
        if (!item) {
          setErrorMessage('Inventory item not found.');
          return;
        }
        setItemDescription(item.item_description || '');
        setDamageDescription(item.damage_description || '');
        if (item.date_of_purchase) {
          const [year, month] = String(item.date_of_purchase).split('-');
          setPurchaseYear(year || '');
          setPurchaseMonth(month || '');
        }
        setPrice(item.price != null ? String(item.price) : '');
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(getErrorMessage(error) || 'Failed to load inventory item.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingItem(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [claimId, editingItemId]);

  const monthYearValue = useMemo(() => {
    if (!purchaseMonth || !purchaseYear) return null;
    return `${purchaseYear}-${purchaseMonth}`;
  }, [purchaseMonth, purchaseYear]);

  const validateEntry = (): string | null => {
    if (!itemDescription.trim()) return 'Item make and model is required.';
    if (!damageDescription.trim()) return 'Description of damage is required.';
    if ((purchaseMonth && !purchaseYear) || (!purchaseMonth && purchaseYear)) {
      return 'Please select both month and year for the purchase date.';
    }
    if (price.trim()) {
      const normalizedPrice = normalizeDecimalInput(price);
      if (!/^\d+(\.\d{1,2})?$/.test(normalizedPrice)) {
        return 'Enter a valid price (up to 2 decimal places).';
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!claimId || !editingItemId) return;

    const validationError = validateEntry();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage('');
    setIsSavingItem(true);
    try {
      const normalizedPrice = normalizeDecimalInput(price);
      await updateInventoryItem(claimId, editingItemId, {
        item_description: itemDescription.trim(),
        damage_description: damageDescription.trim(),
        date_of_purchase: monthYearValue,
        price: normalizedPrice === '' ? null : normalizedPrice,
      });
      showSuccess('Item updated successfully.');
      navigate(listPath);
    } catch (error) {
      const message = getErrorMessage(error) || 'Failed to update inventory item.';
      setErrorMessage(message);
      showError(message);
    } finally {
      setIsSavingItem(false);
    }
  };

  if (!isClaimReady) {
    if (resumeError) {
      return (
        <Layout>
          <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, px: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {resumeError}
            </Alert>
            {claimToken && (
              <Button
                variant="contained"
                onClick={() => navigate(claimStatusReturnPath(claimToken))}
                sx={{ backgroundColor: 'var(--primary-color)' }}
              >
                Back to Claim Status
              </Button>
            )}
          </Box>
        </Layout>
      );
    }

    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </Layout>
    );
  }

  if (isLoadingItem) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      <ProgressSteps currentStep="damages" hide={incompleteFromStatus} />

      <Box
        sx={{
          width: '100%',
          maxWidth: { md: 880, lg: 960 },
          mx: { md: 'auto' },
          px: { md: 0 },
          pb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Edit Item
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Update the item details below, then save your changes.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Item make and model"
            multiline
            minRows={3}
            required
            value={itemDescription}
            onChange={(e) => setItemDescription(e.target.value)}
            placeholder="Example: Sony V35 40in TV"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Description of damage"
            multiline
            minRows={3}
            required
            value={damageDescription}
            onChange={(e) => setDamageDescription(e.target.value)}
            placeholder="Describe what damage occurred to this item"
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" component="div">
              <strong>Purchase Price:</strong>
            </Typography>
            <TextField
              size="small"
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/\$/g, ''))}
              placeholder="0.00"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9]*\\.?[0-9]{0,2}',
                step: '0.01',
                min: '0',
                'aria-label': 'Purchase price in dollars',
              }}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" component="div">
              <strong>Purchase Date:</strong>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}
            >
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 170 }, flex: '0 0 auto' }}>
                <InputLabel id="edit-purchase-month-label">Month</InputLabel>
                <Select
                  labelId="edit-purchase-month-label"
                  value={purchaseMonth}
                  label="Month"
                  onChange={(e) => setPurchaseMonth(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>Select month</em>
                  </MenuItem>
                  {months.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: { xs: '100%', sm: 145 }, flex: '0 0 auto' }}>
                <InputLabel id="edit-purchase-year-label">Year</InputLabel>
                <Select
                  labelId="edit-purchase-year-label"
                  value={purchaseYear}
                  label="Year"
                  onChange={(e) => setPurchaseYear(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>Select year</em>
                  </MenuItem>
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSavingItem}
              sx={{
                color: 'var(--primary-color)',
                borderColor: 'var(--primary-color)',
                fontWeight: 600,
                minWidth: { xs: '100%', sm: 120 },
                '&:hover': { backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSavingItem}
              sx={{
                backgroundColor: 'var(--primary-color)',
                fontWeight: 700,
                minWidth: { xs: '100%', sm: 120 },
                '&:hover': { backgroundColor: 'var(--primary-color)' },
              }}
            >
              {isSavingItem ? <CircularProgress size={22} /> : 'Save'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default EditInventoryItem;
